/**
 * <complete-your-purchase-item>
 *
 * Tarjeta de producto complementario para la ficha de producto:
 *  - El color sigue al color elegido en la ficha.
 *  - El resto de opciones (talla...) se eligen en un desplegable.
 *  - Añadir al carrito con petición propia (no usa product-form-component, ver nota más abajo).
 *
 * Reglas de selección de variante (por orden de prioridad):
 *   1. Mismo color que la ficha + (talla elegida a mano en el desplegable, o misma talla que la ficha).
 *   2. Mismo color que la ficha, primera talla disponible.
 *   3. Resto de opciones sin color, primera variante disponible.
 *   4. Primera variante disponible.
 *
 * Por qué no se usa product-form-component del tema: dentro de la misma sección que el selector de variantes
 * principal, ese componente escucha los cambios de variante del producto principal y, si se pulsa "Añadir"
 * mientras se actualiza, puede acabar añadiendo el producto principal en lugar del complementario.
 * Aquí se replica el flujo del tema (eventos de carrito de @shopify/events) sin esa dependencia.
 */

const PDP_PICKER = 'variant-picker[data-template-product-match="true"]';

/**
 * Nombre de la opción de un control del selector de la ficha. En Horizon los radios NO llevan data-option-name:
 * el nombre va en `name`, con sufijo: "Color-<sectionId>__variant_picker-<productId>".
 * @param {Element} el
 */
const optionKey = (el) => {
  const raw = el.getAttribute('data-option-name') || el.getAttribute('name') || '';
  return norm(raw.replace(/-[^-\s]*__variant_picker-\d+$/, ''));
};

/** @param {unknown} value */
const norm = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase();

/**
 * Asigna texto solo si cambia (evita mutaciones inútiles que reactivarían el observador).
 * @param {Element} el
 * @param {string} text
 */
const setText = (el, text) => {
  if (el.textContent !== text) el.textContent = text;
};

/**
 * @typedef {{ id: number, available: boolean, options: string[], price: string, compare: string | null, image: string | null }} Variant
 * @typedef {{ el: Element, index: number, key: string, isColor: boolean }} Control
 */

class CompleteYourPurchaseItem extends HTMLElement {
  /** Carga los eventos de carrito del tema. Se puede sustituir (pruebas). */
  static loadEvents = () => import('@shopify/events');

  /** Navegación de reserva si los eventos del tema no están disponibles. Se puede sustituir (pruebas). */
  static navigate = (/** @type {string} */ url) => window.location.assign(url);

  /** @type {AbortController | undefined} */
  #abort;

  /** @type {Variant[]} */
  #variants = [];

  /** @type {Variant | undefined} */
  #chosen;

  /** Opciones que el cliente ha cambiado a mano en el desplegable (índice de opción -> valor). */
  /** @type {Map<number, string>} */
  #touched = new Map();

  /** @type {number | undefined} */
  #raf;

  /** Mientras el botón muestra "Añadido" no se le pisa el texto. */
  #flashing = false;

  connectedCallback() {
    const raw = this.querySelector('script[data-variants]')?.textContent;

    try {
      this.#variants = JSON.parse(raw ?? '[]');
    } catch {
      this.#variants = [];
    }
    if (this.#variants.length === 0) return;

    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    // El cliente cambia un desplegable de esta tarjeta.
    this.addEventListener(
      'change',
      (event) => {
        const select = event.target instanceof Element ? event.target.closest('select[data-option-index]') : null;
        if (!(select instanceof HTMLSelectElement)) return;

        this.#touched.set(Number(select.dataset.optionIndex), select.value);
        this.#sync();
      },
      { signal }
    );

    // El cliente pulsa "Añadir".
    this.addEventListener(
      'submit',
      (event) => {
        if (!(event.target instanceof HTMLFormElement)) return;
        event.preventDefault();
        this.#addToCart(event.target);
      },
      { signal }
    );

    // El cliente cambia una opción en la ficha de producto. 'change' burbujea de forma nativa.
    document.addEventListener(
      'change',
      (event) => {
        if (event.target instanceof Element && event.target.closest(PDP_PICKER)) this.#sync();
      },
      { signal }
    );

    // El tema actualiza la ficha por fetch y "morfea" el HTML (selector, precio... y también este bloque, que vuelve
    // a su estado de servidor: variante por defecto). Cada vez que cambie algo en la sección, volvemos a aplicar
    // la selección. El render solo escribe si hay diferencias, así que no entra en bucle.
    const scope = this.closest('.shopify-section') ?? document.body;
    const observer = new MutationObserver(() => this.#scheduleSync());
    observer.observe(scope, { subtree: true, childList: true, attributes: true, characterData: true });
    signal.addEventListener('abort', () => {
      observer.disconnect();
      if (this.#raf !== undefined) cancelAnimationFrame(this.#raf);
    });

    // Cambios de URL (el tema hace history.replaceState con ?variant=...) y botón atrás.
    window.addEventListener('popstate', () => this.#scheduleSync(), { signal });

    this.#sync();
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /** Agrupa varias mutaciones seguidas en una sola sincronización. */
  #scheduleSync() {
    if (this.#raf !== undefined) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = undefined;
      this.#sync();
    });
  }

  /**
   * Opciones seleccionadas ahora mismo en la ficha: { 'color': 'Naranja', 'talla': 'M' }.
   * @returns {Record<string, string>}
   */
  #pdpSelection() {
    /** @type {Record<string, string>} */
    const selection = {};
    const picker = document.querySelector(PDP_PICKER);
    if (!picker) return selection;

    for (const input of picker.querySelectorAll('input[type="radio"]:checked')) {
      if (input instanceof HTMLInputElement) selection[optionKey(input)] = input.value;
    }

    for (const select of picker.querySelectorAll('select')) {
      const option = select.options[select.selectedIndex];
      if (option) selection[optionKey(option.dataset.optionName ? option : select)] = option.value;
    }

    return selection;
  }

  /** @returns {Control[]} */
  #controls() {
    const colorKey = norm(this.dataset.colorOption);

    return Array.from(this.querySelectorAll('[data-option-index]')).map((el) => {
      const key = norm(el.getAttribute('data-option-name'));
      return {
        el,
        index: Number(el.getAttribute('data-option-index')),
        key,
        isColor: key !== '' && key === colorKey,
      };
    });
  }

  #sync() {
    const pdp = this.#pdpSelection();
    const controls = this.#controls();
    const optionCount = this.#variants[0]?.options.length ?? 0;
    const colorIdx = controls.find((control) => control.isColor)?.index ?? -1;

    /** @type {(string | null)[]} */
    const desired = Array.from({ length: optionCount }, () => null);

    for (const control of controls) {
      desired[control.index] = control.isColor
        ? pdp[control.key] ?? null
        : this.#touched.get(control.index) ?? pdp[control.key] ?? null;
    }

    /**
     * @param {Variant} variant
     * @param {(string | null)[]} wanted
     */
    const matches = (variant, wanted) =>
      wanted.every((value, i) => value === null || norm(variant.options[i]) === norm(value));

    const onlyColor = desired.map((value, i) => (i === colorIdx ? value : null));
    const withoutColor = desired.map((value, i) => (i === colorIdx ? null : value));
    const anything = desired.map(() => null);

    /** @type {Variant | undefined} */
    let chosen;
    for (const wanted of [desired, onlyColor, withoutColor, anything]) {
      chosen = this.#variants.find((variant) => variant.available && matches(variant, wanted));
      if (chosen) break;
    }

    // Nada disponible: nos quedamos en el color pedido (aunque esté agotado) para que el cliente lo vea.
    chosen ??= this.#variants.find((variant) => matches(variant, onlyColor)) ?? this.#variants[0];

    if (chosen) this.#render(chosen, controls, colorIdx);
  }

  /**
   * @param {Variant} variant
   * @param {Control[]} controls
   * @param {number} colorIdx
   */
  #render(variant, controls, colorIdx) {
    this.#chosen = variant;

    const colorValue = colorIdx >= 0 ? norm(variant.options[colorIdx]) : null;
    const soldOutLabel = this.dataset.soldOutLabel || '';

    for (const control of controls) {
      if (control.isColor) {
        const label = control.el.querySelector('[data-color-value]');
        if (label) setText(label, variant.options[control.index] ?? '');
        continue;
      }

      if (!(control.el instanceof HTMLSelectElement)) continue;

      for (const option of control.el.options) {
        if (option.dataset.label === undefined) option.dataset.label = option.textContent ?? '';
        const baseLabel = option.dataset.label;

        // Una talla está disponible si existe una variante disponible con ESTE color y esta talla.
        const available = this.#variants.some(
          (candidate) =>
            candidate.available &&
            (colorValue === null || norm(candidate.options[colorIdx]) === colorValue) &&
            norm(candidate.options[control.index]) === norm(option.value)
        );

        if (option.disabled === available) option.disabled = !available;
        setText(option, available || !soldOutLabel ? baseLabel : `${baseLabel} – ${soldOutLabel}`);
      }

      const wantedValue = variant.options[control.index];
      if (wantedValue !== undefined && control.el.value !== wantedValue) control.el.value = wantedValue;
    }

    const price = this.querySelector('[data-price]');
    if (price) setText(price, variant.price);

    const compare = this.querySelector('[data-compare-price]');
    if (compare instanceof HTMLElement) {
      setText(compare, variant.compare ?? '');
      if (compare.hidden !== !variant.compare) compare.hidden = !variant.compare;
    }

    const image = this.querySelector('img[data-media]');
    if (image instanceof HTMLImageElement && variant.image && image.getAttribute('src') !== variant.image) {
      image.src = variant.image;
    }

    // La URL del producto puede traer ya parámetros (p. ej. de seguimiento de recomendaciones).
    const baseUrl = this.dataset.productUrl;
    if (baseUrl) {
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.set('variant', String(variant.id));
      for (const link of this.querySelectorAll('a[data-product-link]')) {
        const href = `${url.pathname}${url.search}`;
        if (link.getAttribute('href') !== href) link.setAttribute('href', href);
      }
    }

    const idInput = this.querySelector('input[name="id"]');
    if (idInput instanceof HTMLInputElement && idInput.value !== String(variant.id)) idInput.value = String(variant.id);

    this.#applyButtonState();
  }

  /**
   * @param {boolean} [busy]
   * @param {boolean} [keepLabel] No tocar el texto (mientras se muestra "Añadido").
   */
  #applyButtonState(busy = false, keepLabel = false) {
    const button = this.querySelector('button[type="submit"]');
    if (!(button instanceof HTMLButtonElement) || !this.#chosen) return;

    const { available } = this.#chosen;
    busy = busy || this.hasAttribute('data-busy');
    keepLabel = keepLabel || this.#flashing;
    if (button.disabled !== (busy || !available)) button.disabled = busy || !available;
    if (keepLabel) return;
    setText(button, (available ? button.dataset.addLabel : button.dataset.soldOutLabel) ?? button.textContent ?? '');
  }

  /** Igual que product-form.js del tema: usa el componente de carrito si existe, si no el JSON del carrito. */
  async #refreshCart() {
    /** @type {any} */
    const cartItems = document.querySelector('cart-items-component');

    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      return cartItems.fetchCartData();
    }

    const cartUrl = /** @type {any} */ (window).Theme?.routes?.cart_url ?? '/cart';
    const response = await fetch(`${cartUrl}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`No se pudo leer el carrito: ${response.status}`);
    return response.json();
  }

  /** @param {HTMLFormElement} form */
  async #addToCart(form) {
    if (this.hasAttribute('data-busy') || !this.#chosen?.available) return;

    this.setAttribute('data-busy', '');
    this.#applyButtonState(true);

    const errorEl = this.querySelector('[data-error]');
    if (errorEl instanceof HTMLElement) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    const Ctor = /** @type {typeof CompleteYourPurchaseItem} */ (this.constructor);
    const theme = /** @type {any} */ (window).Theme;

    /** @type {any} */
    let events = null;
    try {
      events = await Ctor.loadEvents();
    } catch {
      events = null;
    }

    const formData = new FormData(form);

    // Mismas secciones del carrito que pide el tema, para que el cajón se actualice.
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : undefined))
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const variantId = String(formData.get('id') ?? '');
    const deferred = events?.CartLinesUpdateEvent?.createPromise?.() ?? null;

    if (events && deferred) {
      this.dispatchEvent(
        new events.CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: variantId, quantity: 1 }],
          promise: deferred.promise,
        })
      );
    }

    /** @param {any} cart @param {boolean} didError @param {any} [sections] */
    const resolveEvent = (cart, didError, sections) =>
      deferred?.resolve({
        cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'complete-your-purchase',
          sourceId: this.id,
          itemCount: 1,
          productId: this.dataset.productId,
          sections,
          didError,
        },
      });

    let flashing = false;

    try {
      const response = await fetch(theme?.routes?.cart_add_url ?? '/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      }).then((r) => r.json());

      if (response.status) {
        if (events?.CartErrorEvent) {
          this.dispatchEvent(
            new events.CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: { description: response.description, errors: response.errors },
            })
          );
        }

        if (deferred) {
          this.#refreshCart().then((cart) => resolveEvent(cart, true), deferred.reject);
        }

        if (errorEl instanceof HTMLElement) {
          errorEl.textContent = response.description || response.message || '';
          errorEl.hidden = !errorEl.textContent;
        }
        return;
      }

      // Sin los eventos del tema no podemos refrescar el cajón: llevamos al carrito (el producto ya está añadido).
      if (!events || !deferred) {
        Ctor.navigate(theme?.routes?.cart_url ?? '/cart');
        return;
      }

      resolveEvent(await this.#refreshCart(), false, response.sections);

      const added = theme?.translations?.added;
      const button = this.querySelector('button[type="submit"]');
      if (added && button instanceof HTMLButtonElement) {
        flashing = true;
        this.#flashing = true;
        button.textContent = added;
        setTimeout(() => {
          this.#flashing = false;
          this.#applyButtonState();
        }, 1500);
      }
    } catch (error) {
      console.error(error);
      deferred?.reject(error);
      if (events?.CartErrorEvent) {
        this.dispatchEvent(
          new events.CartErrorEvent({
            error: /** @type {any} */ (error)?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.removeAttribute('data-busy');
      this.#applyButtonState(false, flashing);
    }
  }
}

if (!customElements.get('complete-your-purchase-item')) {
  customElements.define('complete-your-purchase-item', CompleteYourPurchaseItem);
}